import { useFeatureIsOn } from '@growthbook/growthbook-react';
import makeStyles from '@mui/styles/makeStyles';
import { FC } from 'react';

import Button from '@/components/Button/Button';
import Divider from '@/components/Divider/Divider';
import Link from '@/components/Link';
import { policies } from '@/core/agreements';
import {
  EXPERT_CONTENT_TREATMENT,
  EXPERT_CONTENT_TREATMENT_HEADING,
  EXPERT_TRANSFER_TREATMENT,
  EXPERT_TRANSFER_TREATMENT_FLAG,
} from '@/core/contentTreatment';
import { teal500 } from '@/theme/colors';
import { formatBillRate } from '@/utils';

import CalendarMenu from '../../CalendarMenu';
import s from './Actions.module.scss';

const useStyles = makeStyles(() => ({
  root: {
    paddingLeft: 12,
    paddingRight: 12,
    marginLeft: 6,
  },
}));

function StyledButton({ children, color, ...other }: any) {
  const classes = useStyles();
  return (
    <Button {...other} classes={classes} variant="text" fontColor={color}>
      {children}
    </Button>
  );
}

const unpaidDisclaimer =
  'This is an unpaid call request. Confirmation of this call is an acknowledgement of the fee structure.';

interface ActionsProps {
  consultation: any;
  isConfirmed: boolean;
  isViewerExpert: boolean;
  isExpertFirstAttempt: boolean;
  canConfirm: boolean;
  canDeny: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  canInviteParticipant: boolean;
  confirmSubmitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDeny: () => void;
  onReschedule: () => void;
  onInviteParticipant: () => void;
}

const Actions: FC<ActionsProps> = ({
  consultation,
  isConfirmed,
  isViewerExpert,
  isExpertFirstAttempt,
  canConfirm,
  canDeny,
  canCancel,
  canReschedule,
  canInviteParticipant,
  confirmSubmitting,
  onConfirm,
  onCancel,
  onDeny,
  onReschedule,
  onInviteParticipant,
}) => {
  const privacyPol = policies.privacy;
  const expertPartPol = policies['expert-participation-agreement'];
  const showTransferTreatment = useFeatureIsOn(EXPERT_TRANSFER_TREATMENT_FLAG);

  const {
    id,
    expected_duration: expectedDuration,
    completion_adds_expert_to_network: completionAddsToNetwork,
    event,
    unpaid,
  } = consultation;

  const showDivider = canConfirm || canCancel || canDeny || canReschedule;

  // The footer names the button the expert is about to press, so both read from
  // here. On a rescheduled or repeat attempt the button is not "Accept Call".
  const acceptLabel = isExpertFirstAttempt ? 'Accept Call' : 'Confirm Time';

  return (
    <div>
      {showDivider && <Divider />}

      {/* One guard for both: the footer says "stated above", so it must not be
          able to render without the block. */}
      {canConfirm && isViewerExpert && (
        <>
          <div className={s.contentTreatment}>
            <h3 className={s.contentTreatmentHeading}>{EXPERT_CONTENT_TREATMENT_HEADING}</h3>
            {EXPERT_CONTENT_TREATMENT}
            {showTransferTreatment && EXPERT_TRANSFER_TREATMENT && (
              <span> {EXPERT_TRANSFER_TREATMENT}</span>
            )}
          </div>

          <div className={s.legalNotice}>
            By clicking “{acceptLabel},” you accept this consultation on the content treatment
            stated above, and you agree that the consultation, including any personal data, may be
            recorded and transcribed by OnFrontiers and used and maintained pursuant to your{' '}
            <Link newTab href={expertPartPol.url}>
              {expertPartPol.name}
            </Link>{' '}
            and OnFrontiers’{' '}
            <Link newTab href={privacyPol.url}>
              {privacyPol.name}
            </Link>
            .
          </div>
        </>
      )}
      {canConfirm && isViewerExpert && completionAddsToNetwork && (
        <div className={s.legalNotice}>
          You will be added to the client&apos;s network after the consultation is completed.
          Clients are able to view your contact information and connect with you more easily when
          you are part of their network. You may remove the connection at the any time.
        </div>
      )}

      <div className={s.actions}>
        {isConfirmed && (
          <CalendarMenu event={event} consultationId={id} expectedDuration={expectedDuration} />
        )}

        {canConfirm && (
          <div className={s.acceptButton}>
            <StyledButton
              color={teal500}
              onClick={onConfirm}
              submitting={confirmSubmitting}
              disabled={confirmSubmitting}
            >
              {acceptLabel}
            </StyledButton>
          </div>
        )}

        {canInviteParticipant && (
          <StyledButton onClick={onInviteParticipant}>Invite Participant</StyledButton>
        )}

        {canDeny && <StyledButton onClick={onDeny}>I&apos;m not available</StyledButton>}

        {canCancel && <StyledButton onClick={onCancel}>Cancel Call</StyledButton>}

        {canReschedule && <StyledButton onClick={onReschedule}>Reschedule Call</StyledButton>}
      </div>

      {canConfirm && isViewerExpert && !unpaid && (
        <div className={s.expertRate}>
          {consultation.bill_rate > 0 && 'Your rate for this consultation is'}{' '}
          {consultation.bill_rate > 0 && formatBillRate(consultation.bill_rate)}{' '}
          {consultation.bill_rate === 0 && unpaidDisclaimer}{' '}
          <Link newTab href={expertPartPol.url}>
            Learn more.
          </Link>
        </div>
      )}
    </div>
  );
};

export default Actions;
